import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Api, ApiService} from "../../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {Modal, ModalService} from "../../../../../../services/modal.service";
import {StorageService} from "../../../../../../../common/services/storage.service";
import {Base} from "../../../../../../../common/interfaces/base.interfaces";
import {UserService} from "../../../../../../../common/services/user.service";
import {FileUploadComponent} from "../../../../../../../common/components/file-upload/file-upload.component";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {AppStateService} from "../../../../../../../common/services/app-state.service";
import {Form} from "../../../../../../../common/interfaces/form.interface";
import ISelectOption = Form.ISelectOption;


@Component({
    selector: "section-quality-request-form",
    templateUrl: "form.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class QualityRequestFormComponent implements OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    @Input()
    public data: any;

    @Output()
    public onSubmit: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onClose: EventEmitter<null> = new EventEmitter<null>();

    @Output()
    public onMinimize: EventEmitter<null> = new EventEmitter<null>();

    /**
     * Form group / group with controls
     * @type {FormGroup}
     */
    public formGroup: FormGroup = new FormGroup({
        title: new FormControl(null, [Validators.required]),
        description: new FormControl(null, [Validators.required]),
        request_kind_id: new FormControl(null, [Validators.required]),
        priority: new FormControl("Normal", [Validators.required]),
        status: new FormControl("New"),
        order_id: new FormControl(null),
        order_remark_type_id: new FormControl(4),
        assignee_id: new FormControl(535),
        url: new FormControl(window.location.pathname),
    });

    public state: Base.IState;

    public partners: ISelectOption[];

    public kinds: ISelectOption[] = [];

    public showOrderLink: boolean = false;

    public uploadedFile: ISelectOption[] = [];

    public editorInitialValue: string = null;

    public currentUser: any = null;

    public showRelatedPartners = false;


    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private apiService: ApiService,
        private toastService: ToastService,
        private modalService: ModalService,
        private storageService: StorageService,
        private userService: UserService,
        private spinnerService: SpinnerService,
    ) {
        this.currentUser = this.userService.data;
    }

    /**
     * Prepare form data
     */
    private prepareData(): void {

        if (this.data) {
            this.formGroup.patchValue(this.data);
            this.editorInitialValue = this.data.description;

            if (this.data.remark_id) {
                this.formGroup.removeControl("description");
                this.formGroup.updateValueAndValidity();
                this.formGroup.addControl("order_remark_id", new FormControl(this.data.remark_id));
            }
        }

        if (this.state.component === "orders" && this.state.action === "view") {
            this.showOrderLink = true;
            this.formGroup.get("order_id").setValue(this.state.params.id);
        }

        if (this.storageService.get("requestOperations")) {
            this.formGroup.patchValue(this.storageService.get("requestOperations"));
            this.editorInitialValue = this.formGroup.value.description;
        }

        if (this.storageService.get("requestOperationsFiles")) {
            this.uploadedFile = this.storageService.get("requestOperationsFiles");
        }
    }


    /**
     * Get partners list
     * @returns {Promise<any>}
     */
    private async getPartners(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["warehouse", "partners"], {}, {data_structure: "select"});
        this.spinnerService.hide();
        if (data) {
            this.partners = data;
            this.changeDetectorRef.markForCheck();
        }
    }


    /**
     * Get list of kinds
     * @returns {Promise<any>}
     */
    private async getKinds(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["request", "kind"], {}, {
                category: "quality_event",
                data_structure: "select",
            });
        if (data) {
            this.kinds = data;
            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }


    public async uploadFile(): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(FileUploadComponent, {
            url: ["user", "file"],
            accept: [
                "gif", "png", "jpg", "jpeg", "tiff", "doc",
                "docx", "xls", "xlsx", "csv", "ppt", "pptx", "msg", "txt", "pdf", "xlsb"
            ]
        });
        if (response && response.value && response.value.data && response.value.data.path) {
            const uploaded: any = {name: response.value.data.name, url: response.value.data.path};
            this.uploadedFile.push(uploaded);
            this.changeDetectorRef.markForCheck();
        }
    }

    public deleteFile(index: number): void {
        this.uploadedFile.splice(index, 1);
    }


    /**
     * Submit form
     */
    public async submit(): Promise<any> {
        this.spinnerService.show();

        const body: any = {...this.formGroup.value};

        body.files = this.uploadedFile;


        body.type = "quality_event";
        const response: Api.IResponse = await this.apiService.request(Api.EMethod.Post, ["request"], body);

        if (response && response.type as string === "success") {
            this.toastService.show(response.message, response.type as string);
            this.onSubmit.emit(response.data);
        }
        this.spinnerService.hide();

        this.storageService.remove("requestOperations");
        this.storageService.remove("requestOperationsFiles");
    }

    public close(): void {
        this.onClose.emit();
    }

    public minimize(): void {
        this.storageService.set("isRequestMinimized", true);
        this.onMinimize.emit();
    }

    public ngOnInit(): void {

        this.state = AppStateService.getState();

        this.getKinds();
        this.getPartners();

        this.changeDetectorRef.markForCheck();

        if ((this.state && this.state.section.includes("warehouse"))) {
            this.formGroup.addControl("related_partner", new FormControl(null, [Validators.required]));
            this.changeDetectorRef.markForCheck();
            this.getPartners();
            this.showRelatedPartners = true;
        }
        this.prepareData();
    }

    public ngOnDestroy(): void {
        this.storageService.set("requestQuality", this.formGroup.value);
        this.storageService.set("requestQualityFiles", this.uploadedFile);
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
